import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Home.css";

// --- Curated base64-style placeholder images for each sector (lightweight SVG data URIs) ---
const SECTOR_IMAGES = {
  // Wellness / Spa / Beauty
  "bien": "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=300&q=60",
  "spa": "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=300&q=60",
  "beaute": "https://images.unsplash.com/photo-1560066984-138dadb4c035?w=300&q=60",
  "coiffure": "https://images.unsplash.com/photo-1562322140-8baeececf3df?w=300&q=60",
  "sport": "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=300&q=60",
  "fitness": "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=300&q=60",
  "restaurant": "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=300&q=60",
  "food": "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=300&q=60",
  "loisir": "https://images.unsplash.com/photo-1526495124232-a04e1849168c?w=300&q=60",
  "enfant": "https://images.unsplash.com/photo-1526495124232-a04e1849168c?w=300&q=60",
  "parc": "https://images.unsplash.com/photo-1526495124232-a04e1849168c?w=300&q=60",
  "sante": "https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=300&q=60",
  "medical": "https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=300&q=60",
  "education": "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=300&q=60",
  "default": "https://images.unsplash.com/photo-1497366216548-37526070297c?w=300&q=60",
};

const getSectorImage = (name = "", emoji = "") => {
  if (!name) return SECTOR_IMAGES.default;
  const lower = name.toLowerCase();
  for (const [key, url] of Object.entries(SECTOR_IMAGES)) {
    if (key !== "default" && lower.includes(key)) return url;
  }
  return SECTOR_IMAGES.default;
};

const PROVIDER_PLACEHOLDER = "https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&q=70";

function Home() {
  const [providers, setProviders] = useState([]);
  const [families, setFamilies] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLocation, setSearchLocation] = useState("");
  const [selectedFamily, setSelectedFamily] = useState("All");
  const [selectedActivity, setSelectedActivity] = useState("Tous");
  const [userFavorites, setUserFavorites] = useState([]);

  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    if (token && user) {
      if (user.role === 'provider') {
        navigate('/ProviderDashboard');
      } else if (user.role === 'admin' || user.role === 'useradmin') {
        navigate('/AdminApprovals');
      }
    }
    fetchData();
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [famRes, actRes, providersRes] = await Promise.all([
        axios.get('/api/activities-hub/families'),
        axios.get('/api/activities-hub/activities'),
        axios.get('/api/auth/AllUser')
      ]);

      setFamilies(famRes.data);
      setActivities(actRes.data);

      const approvedProviders = (providersRes.data.AllUser || []).filter(
        u => u.role === 'provider' && u.isApproved === 'approved'
      );
      setProviders(approvedProviders);

      if (token) {
        const favRes = await axios.get('/api/auth/favorites', {
          headers: { Authorization: token }
        });
        setUserFavorites((favRes.data || []).map(f => f._id || f));
      }
    } catch (err) {
      console.error("Error loading home page data:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleFav = async (providerId) => {
    if (!token) {
      alert("Veuillez vous connecter pour ajouter des favoris.");
      return;
    }
    try {
      const res = await axios.post(`/api/auth/favorites/${providerId}`, {}, {
        headers: { Authorization: token }
      });
      setUserFavorites(res.data.favorites || []);
    } catch (err) {
      console.error("Error toggling favorite:", err);
    }
  };

  // Group activities by family id dynamically
  const activitiesByFamily = {};
  families.forEach(fam => {
    activitiesByFamily[fam._id] = activities.filter(
      act => String(act.family?._id || act.family) === String(fam._id)
    );
  });

  const getProviderFamilyId = (p) => {
    if (p.activity && typeof p.activity === 'object') {
      const famId = p.activity.family?._id || p.activity.family;
      return famId ? String(famId) : null;
    }
    if (p.activity && typeof p.activity === 'string') {
      const pActLower = p.activity.toLowerCase();
      const foundAct = activities.find(a => {
        const aNameLower = a.name.toLowerCase();
        return aNameLower === pActLower || 
               aNameLower.includes(pActLower) || 
               pActLower.includes(aNameLower) ||
               String(a._id) === String(p.activity);
      });
      if (foundAct) {
        const famId = foundAct.family?._id || foundAct.family;
        return famId ? String(famId) : null;
      }
    }
    return null;
  };

  const filteredProviders = providers.filter(p => {
    const pActivityName = p.activity && typeof p.activity === 'object' ? p.activity.name : p.activity;
    const resolvedFamilyId = getProviderFamilyId(p);

    if (selectedFamily !== "All" && String(resolvedFamilyId) !== String(selectedFamily)) return false;
    if (selectedActivity !== "Tous") {
      const pActLower = pActivityName?.toLowerCase() || "";
      const selActLower = selectedActivity.toLowerCase();
      const isActivityMatch = pActLower === selActLower || pActLower.includes(selActLower) || selActLower.includes(pActLower);
      if (!isActivityMatch) return false;
    }
    if (searchQuery.trim()) {
      const nameMatch = p.businessName?.toLowerCase().includes(searchQuery.toLowerCase());
      const activityMatch = pActivityName?.toLowerCase().includes(searchQuery.toLowerCase());
      if (!nameMatch && !activityMatch) return false;
    }
    if (searchLocation.trim() && p.adress) {
      const locMatch = p.adress.toLowerCase().includes(searchLocation.toLowerCase());
      if (!locMatch) return false;
    }
    return true;
  });

  const getActivityDetails = (actObjOrStr) => {
    if (actObjOrStr && typeof actObjOrStr === 'object') {
      return { label: actObjOrStr.name, emoji: actObjOrStr.emoji };
    }
    if (actObjOrStr && typeof actObjOrStr === 'string') {
      const actLower = actObjOrStr.toLowerCase();
      const found = activities.find(a => {
        const aNameLower = a.name.toLowerCase();
        return aNameLower === actLower || aNameLower.includes(actLower) || actLower.includes(aNameLower);
      });
      if (found) {
        return { label: found.name, emoji: found.emoji };
      }
    }
    return { label: actObjOrStr || 'Service', emoji: '💼' };
  };

  const renderStars = (rating = 5) => {
    return "★".repeat(Math.round(rating)) + "☆".repeat(5 - Math.round(rating));
  };

  const renderProviderCard = (p) => {
    const actInfo = getActivityDetails(p.activity);
    const heroImg = p.logo || getSectorImage(actInfo.label);
    return (
      <Col key={p._id} xs={6} md={4} lg={3}>
        <div className="provider-square-card" onClick={() => navigate(`/Boutique/${p._id}`)}>
          <div className="provider-square-img-wrap">
            <img
              src={heroImg}
              alt={p.businessName || p.name}
              className="provider-square-img"
              onError={e => { e.target.src = PROVIDER_PLACEHOLDER; }}
            />
            <div className="provider-square-overlay">
              <div className="provider-square-act-badge">{actInfo.emoji} {actInfo.label}</div>
            </div>
            {token && (
              <button
                className="provider-fav-btn"
                onClick={e => { e.stopPropagation(); toggleFav(p._id); }}
              >
                {userFavorites.includes(p._id) ? '❤️' : '🤍'}
              </button>
            )}
          </div>
          <div className="provider-square-info">
            <div className="provider-square-name">{p.businessName || p.name}</div>
            <div className="provider-square-stars text-warning" style={{ fontSize: '13px' }}>
              {renderStars(p.rating)} <span className="text-muted small">({p.rating || 5})</span>
            </div>
            <div className="provider-square-addr text-muted small">📍 {p.adress || "Tunis"}</div>
          </div>
        </div>
      </Col>
    );
  };

  return (
    <div className="marketplace-home">
      {/* Hero Banner Section */}
      <div className="marketplace-hero text-center text-white py-5">
        <Container>
          <h1 className="display-4 font-weight-bold mb-3">
            Réservez vos activités & services préférés
          </h1>
          <p className="lead mb-4">
            Découvrez et réservez des créneaux en temps réel dans votre région.
          </p>

          {/* Search Bar Container */}
          <div className="search-bar-container mx-auto shadow-lg bg-white p-3 rounded-pill d-flex flex-column flex-md-row gap-2 max-width-800">
            <Form.Control
              type="text"
              placeholder="Que recherchez-vous ? (ex: Spa, Fitness)"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="border-0 px-4 rounded-pill search-query-input"
            />
            <div className="vr d-none d-md-block"></div>
            <Form.Control
              type="text"
              placeholder="📍 Ville ou Code Postal"
              value={searchLocation}
              onChange={e => setSearchLocation(e.target.value)}
              className="border-0 px-4 rounded-pill search-location-input"
            />
            <Button variant="primary" className="rounded-pill px-5 search-submit-btn">Rechercher</Button>
          </div>
        </Container>
      </div>

      <Container className="my-5">
        {/* Horizontal Scrollable Families - Now Square Image Cards */}
        <h4 className="font-weight-bold text-secondary mb-4">Explorez par Secteur</h4>
        <div className="categories-scroll-wrapper mb-3 py-2">
          {/* Default 'Toutes' Square */}
          <div
            className={`category-square-btn ${selectedFamily === "All" ? 'active' : ''}`}
            onClick={() => { setSelectedFamily("All"); setSelectedActivity("Tous"); }}
          >
            <div className="category-square-img-wrap">
              <img
                src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=200&q=60"
                alt="Toutes"
                className="category-square-img"
              />
              <div className="category-square-gradient"></div>
            </div>
            <div className="category-square-label">✨ Toutes</div>
          </div>

          {/* Dynamic DB Families */}
          {families.map(fam => (
            <div
              key={fam._id}
              className={`category-square-btn ${selectedFamily === fam._id ? 'active' : ''}`}
              onClick={() => { setSelectedFamily(fam._id); setSelectedActivity("Tous"); }}
            >
              <div className="category-square-img-wrap">
                <img
                  src={fam.imageUrl || getSectorImage(fam.name, fam.emoji)}
                  alt={fam.name}
                  className="category-square-img"
                  onError={e => { e.target.src = SECTOR_IMAGES.default; }}
                />
                <div className="category-square-gradient"></div>
                <span className="category-square-emoji">{fam.emoji}</span>
              </div>
              <div className="category-square-label">{fam.name}</div>
            </div>
          ))}
        </div>

        {/* Secondary Sub-Activities Row - Square style */}
        {selectedFamily !== "All" && (
          <div className="subact-scroll-wrapper mb-5 py-2">
            <div
              className={`subact-square-btn ${selectedActivity === "Tous" ? 'active' : ''}`}
              onClick={() => setSelectedActivity("Tous")}
            >
              <div className="subact-square-icon">🌟</div>
              <div className="subact-square-label">Tout voir</div>
            </div>
            {(activitiesByFamily[selectedFamily] || []).map(act => (
              <div
                key={act._id}
                className={`subact-square-btn ${selectedActivity === act.name ? 'active' : ''}`}
                onClick={() => setSelectedActivity(act.name)}
              >
                <div className="subact-square-img-wrap">
                  <img
                    src={act.imageUrl || getSectorImage(act.name, act.emoji)}
                    alt={act.name}
                    className="subact-square-img"
                    onError={e => { e.target.src = SECTOR_IMAGES.default; }}
                  />
                  <div className="subact-square-gradient"></div>
                </div>
                <div className="subact-square-label">{act.emoji} {act.name}</div>
              </div>
            ))}
          </div>
        )}

        {/* Providers Listing */}
        <h4 className="font-weight-bold text-secondary mb-4">Prestataires disponibles</h4>

        {loading ? (
          <div className="text-center py-5 text-muted">Recherche des meilleurs établissements...</div>
        ) : filteredProviders.length === 0 ? (
          <div className="text-center py-5 my-4 bg-light rounded-4 text-muted">
            <div className="display-4 mb-3">🔍</div>
            Aucun établissement ne correspond à votre recherche pour le moment.
          </div>
        ) : selectedFamily !== "All" ? (
          <Row className="g-3">
            {filteredProviders.map(p => renderProviderCard(p))}
          </Row>
        ) : (
          <>
            {families.map(fam => {
              const famProviders = filteredProviders.filter(p => String(getProviderFamilyId(p)) === String(fam._id));
              if (famProviders.length === 0) return null;
              return (
                <div key={fam._id} className="mb-5">
                  <div className="d-flex align-items-center mb-3">
                    <span className="fs-3 me-2">{fam.emoji}</span>
                    <h4 className="font-weight-bold text-secondary mb-0">{fam.name}</h4>
                    <Badge bg="secondary-subtle" className="text-secondary ms-3 px-3 py-1 rounded-pill font-weight-bold" style={{ fontSize: '12px' }}>
                      {famProviders.length} établissement{famProviders.length > 1 ? 's' : ''}
                    </Badge>
                  </div>
                  <Row className="g-3">
                    {famProviders.map(p => renderProviderCard(p))}
                  </Row>
                </div>
              );
            })}
            
            {(() => {
              const unassignedProviders = filteredProviders.filter(p => !getProviderFamilyId(p));
              if (unassignedProviders.length === 0) return null;
              return (
                <div className="mb-5">
                  <div className="d-flex align-items-center mb-3">
                    <span className="fs-3 me-2">💼</span>
                    <h4 className="font-weight-bold text-secondary mb-0">Autres Établissements</h4>
                    <Badge bg="secondary-subtle" className="text-secondary ms-3 px-3 py-1 rounded-pill font-weight-bold" style={{ fontSize: '12px' }}>
                      {unassignedProviders.length} établissement{unassignedProviders.length > 1 ? 's' : ''}
                    </Badge>
                  </div>
                  <Row className="g-3">
                    {unassignedProviders.map(p => renderProviderCard(p))}
                  </Row>
                </div>
              );
            })()}
          </>
        )}
      </Container>
    </div>
  );
}

export default Home;
